// App-wide error boundary. Without one, ANY render-time throw (a malformed DTO field, a
// charting-library error, an unexpected null in a report subcomponent) unmounts the whole React
// tree to a blank page with no recovery but a manual reload. This catches the throw, shows a
// recoverable fallback, and lets the user reset the subtree or reload.

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional label so a boundary around a subtree (e.g. a chart) names what failed. */
  section?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console for diagnosis; a real telemetry sink could hook in here.
    console.error("[ErrorBoundary]", this.props.section ?? "app", error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="grid place-items-center p-8 min-h-[200px]">
          <div className="max-w-md w-full rounded-2xl border border-destructive/40 bg-destructive/5 p-5 text-center">
            <p className="text-sm font-semibold text-destructive">
              Something went wrong{this.props.section ? ` in ${this.props.section}` : ""}.
            </p>
            <p className="text-xs text-muted-foreground mt-1 break-words">
              {this.state.error.message || "An unexpected error occurred."}
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={this.reset}
                className="px-3 h-8 rounded-lg border border-border bg-card-elevated text-xs font-semibold hover:border-primary/50 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-3 h-8 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
              >
                Reload
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
